import { useState, useEffect } from 'react';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';

import { Edit, Eye, EyeOff, Grid3x3, Layers, Plus, Sparkles, Tag, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [browseSections, setBrowseSections] = useState([]);
  const [tags, setTags] = useState([]);
  const [filterTag, setFilterTag] = useState('all');
  const [newTag, setNewTag] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showBrowseSectionDialog, setShowBrowseSectionDialog] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingBrowseSection, setEditingBrowseSection] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [deleteCategoryModal, setDeleteCategoryModal] = useState({ isOpen: false, categoryId: null });
  const [deleteBrowseSectionModal, setDeleteBrowseSectionModal] = useState({ isOpen: false, sectionId: null });
  const [deleteTagModal, setDeleteTagModal] = useState({ isOpen: false, tagId: null });
  
  // Tab state
  const [activeTab, setActiveTab] = useState('categories'); // 'categories', 'browse-sections', or 'tags'
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ai_prompt: '',
    is_active: true
  });
  const [browseSectionFormData, setBrowseSectionFormData] = useState({
    title: '',
    description: '',
    categories: [],
    tags: '',
    is_active: true,
    order: 0
  });
  const [tagFormData, setTagFormData] = useState({
    name: '',
    description: '',
    type: 'product_tag',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { };
      
      const [categoriesData, browseSectionsData, tagsData, productsData] = await Promise.all([
        fetch(`${api}/admin/categories`, { headers }).then(r => r.json()),
        fetch(`${api}/admin/browse-sections`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${api}/admin/tags-manage`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${api}/admin/products`, { headers }).then(r => r.json())
      ]);
      
      setCategories(categoriesData);
      setBrowseSections(browseSectionsData);
      setTags(tagsData);
      setProducts(productsData);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingCategory 
        ? `${api}/admin/categories/${editingCategory.id}`
        : `${api}/admin/categories`;
      
      const response = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save category');

      toast.success(editingCategory ? 'Category updated' : 'Category created');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBrowseSectionSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingBrowseSection 
        ? `${api}/admin/browse-sections/${editingBrowseSection.id}`
        : `${api}/admin/browse-sections`;
      
      const payload = {
        ...browseSectionFormData,
        tags: browseSectionFormData.tags
          .split(',')
          .map(t => t.trim())
          .filter(t => t.length > 0)
      };
      
      const response = await fetch(url, {
        method: editingBrowseSection ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to save browse section');

      toast.success(editingBrowseSection ? 'Browse section updated' : 'Browse section created');
      setShowBrowseSectionDialog(false);
      resetBrowseSectionForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    setDeleteCategoryModal({ isOpen: false, categoryId: null });
    
    try {
      const response = await fetch(`${api}/admin/categories/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete category');

      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteBrowseSection = async (id) => {
    setDeleteBrowseSectionModal({ isOpen: false, sectionId: null });
    
    try {
      const response = await fetch(`${api}/admin/browse-sections/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete browse section');

      toast.success('Browse section deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggle = async (id) => {
    try {
      await fetch(`${api}/admin/categories/${id}/toggle`, {
        method: 'PATCH',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle category');
    }
  };

  const handleToggleBrowseSection = async (id) => {
    try {
      await fetch(`${api}/admin/browse-sections/${id}/toggle`, {
        method: 'PATCH',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle browse section');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      ai_prompt: category.ai_prompt || '',
      is_active: category.is_active
    });
    setShowDialog(true);
  };

  const handleEditBrowseSection = (section) => {
    setEditingBrowseSection(section);
    setBrowseSectionFormData({
      title: section.title,
      description: section.description || '',
      categories: section.categories || [],
      tags: (section.tags || []).join(', '),
      is_active: section.is_active,
      order: section.order || 0
    });
    setShowBrowseSectionDialog(true);
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      ai_prompt: '',
      is_active: true
    });
  };

  const resetBrowseSectionForm = () => {
    setEditingBrowseSection(null);
    setBrowseSectionFormData({
      title: '',
      description: '',
      categories: [],
      tags: '',
      is_active: true,
      order: 0
    });
  };

  const handleTagSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingTag 
        ? `${api}/admin/tags-manage/${editingTag.id}`
        : `${api}/admin/tags-manage`;
      
      const response = await fetch(url, {
        method: editingTag ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tagFormData)
      });

      if (!response.ok) throw new Error('Failed to save tag');

      toast.success(editingTag ? 'Tag updated' : 'Tag created');
      setShowTagDialog(false);
      resetTagForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteTag = async (tagId) => {
    
    try {
      const response = await fetch(`${api}/admin/tags-manage/${tagId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete tag');

      toast.success('Tag deleted');
      setDeleteTagModal({ isOpen: false, tagId: null });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setTagFormData({
      name: tag.name,
      description: tag.description || '',
      is_active: tag.is_active
    });
    setShowTagDialog(true);
  };

  const resetTagForm = () => {
    setEditingTag(null);
    setTagFormData({
      name: '',
      description: '',
      is_active: true
    });
  };

  // Tag management handlers were deprecated and removed (Feb 2026), they
  // referenced an `addingType` state that no longer exists. All tag CRUD now
  // lives in the main tag dialog/table flows below.

  const toggleCategoryInBrowseSection = (categoryName) => {
    setBrowseSectionFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryName)
        ? prev.categories.filter(c => c !== categoryName)
        : [...prev.categories, categoryName]
    }));
  };

  // Helper function: Calculate product count for a browse section
  const getProductCountForSection = (section) => {
    return products.filter(p => {
      // Filter by categories
      if (section.categories && section.categories.length > 0) {
        if (!section.categories.includes(p.category)) return false;
      }
      
      // Filter by tags (if product has matching tags)
      if (section.tags && section.tags.length > 0) {
        const productTags = p.tags || [];
        const hasMatchingTag = section.tags.some(tag => 
          productTags.some(pTag => pTag.toLowerCase().includes(tag.toLowerCase()))
        );
        if (section.categories.length === 0 && !hasMatchingTag) return false;
      }
      
      return true;
    }).length;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-300 rounded w-1/4"></div>
          <div className="h-64 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: '#20212B' }}>Categories & Sections</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Manage product categories and browse page sections</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 bg-white rounded-xl p-1 sm:p-2" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        <div className="grid grid-cols-3 gap-1 sm:gap-2">
          <button
            onClick={() => setActiveTab('categories')}
            className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
            style={{
              background: activeTab === 'categories' ? '#9E6752' : 'transparent',
              color: activeTab === 'categories' ? 'white' : '#534145'
            }}
          >
            <Tag className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            Categories ({categories.length})
          </button>
          <button
            onClick={() => setActiveTab('browse-sections')}
            className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
            style={{
              background: activeTab === 'browse-sections' ? '#2D4354' : 'transparent',
              color: activeTab === 'browse-sections' ? 'white' : '#534145'
            }}
          >
            <Layers className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            Browse Sections ({browseSections.length})
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
            style={{
              background: activeTab === 'tags' ? '#FED7A5' : 'transparent',
              color: activeTab === 'tags' ? '#20212B' : '#534145'
            }}
          >
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            Tags ({tags.length})
          </button>
        </div>
      </div>

      {/* Product Categories Section */}
      {activeTab === 'categories' && (
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold" style={{ color: '#20212B' }}>Product Categories</h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage product categories and AI behavior</p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
              style={{ background: '#9E6752', color: 'white' }}
              className="text-xs sm:text-sm px-3 sm:px-4"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add Category</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>

          <div className="bg-white rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ background: '#F5F5F5' }}>
                  <tr>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold" style={{ color: '#534145' }}>Name</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold hidden md:table-cell" style={{ color: '#534145' }}>Description</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold hidden lg:table-cell" style={{ color: '#534145' }}>AI Prompt</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold" style={{ color: '#534145' }}>Products</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold hidden sm:table-cell" style={{ color: '#534145' }}>Status</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-right text-xs sm:text-sm font-semibold" style={{ color: '#534145' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category, idx) => (
                    <tr key={category.id} style={{ borderTop: idx > 0 ? '1px solid #E5E5E5' : 'none' }}>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <p className="font-medium text-sm sm:text-base" style={{ color: '#20212B' }}>{category.name}</p>
                        <span className="sm:hidden px-2 py-1 rounded-full text-xs font-medium mt-1 inline-block"
                          style={{
                            background: category.is_active ? '#D1FAE5' : '#FEE2E2',
                            color: category.is_active ? '#065F46' : '#991B1B'
                          }}
                        >
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
                        <p className="text-xs sm:text-sm" style={{ color: '#73766A' }}>
                          {category.description || '-'}
                        </p>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 hidden lg:table-cell">
                        {category.ai_prompt ? (
                          <div className="flex items-center gap-1 text-xs sm:text-sm" style={{ color: '#9E6752' }}>
                            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span>Custom</span>
                          </div>
                        ) : (
                          <span className="text-xs sm:text-sm text-gray-400">Default</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base" style={{ color: '#534145' }}>{category.product_count || 0}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
                        <span 
                          className="px-2 sm:px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: category.is_active ? '#D1FAE5' : '#FEE2E2',
                            color: category.is_active ? '#065F46' : '#991B1B'
                          }}
                        >
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="flex items-center justify-end gap-1 sm:gap-2">
                          <button
                            onClick={() => handleToggle(category.id)}
                            className="p-1.5 sm:p-2 rounded hover:bg-gray-100"
                            title={category.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {category.is_active ? <Eye className="w-3 h-3 sm:w-4 sm:h-4" /> : <EyeOff className="w-3 h-3 sm:w-4 sm:h-4" />}
                          </button>
                          <button
                            onClick={() => handleEdit(category)}
                            className="p-1.5 sm:p-2 rounded hover:bg-[#FED7A5]/40 text-[#2D4354]"
                            title="Edit"
                          >
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteCategoryModal({ isOpen: true, categoryId: category.id })}
                            className="p-1.5 sm:p-2 rounded hover:bg-red-100 text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Browse Sections */}
      {activeTab === 'browse-sections' && (
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: '#20212B' }}>
                <Layers className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#2D4354' }} />
                Browse Page Sections
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Configure sections displayed on the browse page</p>
            </div>
            <Button
              onClick={() => {
                resetBrowseSectionForm();
                setShowBrowseSectionDialog(true);
              }}
              style={{ background: '#2D4354', color: 'white' }}
              className="text-xs sm:text-sm px-3 sm:px-4"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Add Browse Section</span>
              <span className="sm:hidden">Add Section</span>
            </Button>
          </div>

          {browseSections.length === 0 ? (
            <div className="bg-white rounded-xl p-8 sm:p-12 text-center" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <Layers className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3" style={{ color: '#2D4354', opacity: 0.5 }} />
              <p className="text-sm sm:text-base text-gray-600">No browse sections created yet</p>
              <p className="text-xs sm:text-sm text-gray-500">Create sections like "For the Tech Enthusiast" to organize your browse page</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {browseSections.map(section => {
                const productCount = getProductCountForSection(section);
                
                return (
                  <div key={section.id} className="bg-white rounded-xl p-3 sm:p-4 hover:shadow-lg transition-shadow" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                    <div className="flex justify-between items-start mb-2 sm:mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-base sm:text-lg" style={{ color: '#20212B' }}>{section.title}</h3>
                          <span 
                            className="px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium"
                            style={{
                              background: section.is_active ? '#D1FAE5' : '#FEE2E2',
                              color: section.is_active ? '#065F46' : '#991B1B'
                            }}
                          >
                            {section.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm mb-2" style={{ color: '#73766A' }}>{section.description}</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {section.categories?.map(cat => (
                            <span key={cat} className="px-2 py-1 rounded-full text-xs" style={{ background: '#FED7A5', color: '#534145' }}>
                              {cat}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs" style={{ color: '#9E6752' }}>{productCount} product{productCount !== 1 ? 's' : ''}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleToggleBrowseSection(section.id)}
                          className="p-1.5 sm:p-2 rounded hover:bg-gray-100"
                          title={section.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {section.is_active ? <Eye className="w-3 h-3 sm:w-4 sm:h-4" /> : <EyeOff className="w-3 h-3 sm:w-4 sm:h-4" />}
                        </button>
                        <button
                          onClick={() => handleEditBrowseSection(section)}
                          className="p-1.5 sm:p-2 rounded hover:bg-[#FED7A5]/40 text-[#2D4354]"
                          title="Edit"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteBrowseSectionModal({ isOpen: true, sectionId: section.id })}
                          className="p-1.5 sm:p-2 rounded hover:bg-red-100 text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tags Tab Content */}
      {activeTab === 'tags' && (
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" style={{ color: '#20212B' }}>
                <Tag className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#FED7A5' }} />
                Tags & Metadata Management
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage all tags for products and AI recommendations</p>
            </div>
            <Button
              onClick={() => {
                setTagFormData({ name: '', description: '', type: 'product_tag', is_active: true });
                setEditingTag(null);
                setShowTagDialog(true);
              }}
              style={{ background: '#FED7A5', color: '#20212B' }}
              className="text-xs sm:text-sm px-3 sm:px-4"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Add Tag
            </Button>
          </div>

          {/* Filter by Type */}
          <div className="mb-4 flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'All Tags', icon: '🏷️' },
              { value: 'product_tag', label: 'Product Tags', icon: '🛍️' },
              { value: 'interest', label: 'Interests', icon: '❤️' },
              { value: 'personality', label: 'Personality', icon: '🧠' },
              { value: 'occasion', label: 'Occasions', icon: '🎉' },
              { value: 'section_tag', label: 'Section Tags', icon: '🎨' }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setFilterTag(filter.value)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterTag === filter.value
                    ? 'font-semibold'
                    : 'hover:bg-gray-100'
                }`}
                style={{
                  background: filterTag === filter.value ? '#FED7A5' : 'white',
                  color: filterTag === filter.value ? '#20212B' : '#534145',
                  border: '1px solid #e5e7eb'
                }}
              >
                {filter.icon} {filter.label}
              </button>
            ))}
          </div>

          {tags.length === 0 ? (
            <div className="bg-white rounded-xl p-8 sm:p-12 text-center" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <Tag className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3" style={{ color: '#FED7A5', opacity: 0.5 }} />
              <p className="text-sm sm:text-base text-gray-600">No tags created yet</p>
              <p className="text-xs sm:text-sm text-gray-500">Create tags to organize products and improve AI recommendations</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b" style={{ background: '#F9FAFB' }}>
                    <tr>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-semibold" style={{ color: '#20212B' }}>Name</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-semibold" style={{ color: '#20212B' }}>Type</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-semibold hidden sm:table-cell" style={{ color: '#20212B' }}>Description</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-semibold hidden md:table-cell" style={{ color: '#20212B' }}>Products</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-semibold" style={{ color: '#20212B' }}>Status</th>
                      <th className="text-left p-3 sm:p-4 text-xs sm:text-sm font-semibold" style={{ color: '#20212B' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tags
                      .filter(tag => filterTag === 'all' || tag.type === filterTag)
                      .map(tag => (
                      <tr key={tag.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 sm:p-4">
                          <span className="font-medium text-xs sm:text-sm" style={{ color: '#20212B' }}>{tag.name}</span>
                        </td>
                        <td className="p-3 sm:p-4">
                          <span className="px-2 py-1 rounded-full text-xs" style={{
                            background: tag.type === 'product_tag' ? '#E0E7FF' :
                                       tag.type === 'interest' ? '#FED7AA' :
                                       tag.type === 'personality' ? '#FECACA' :
                                       tag.type === 'occasion' ? '#D1FAE5' : '#E5E7EB',
                            color: '#374151'
                          }}>
                            {tag.type === 'product_tag' ? '🛍️ Product' :
                             tag.type === 'interest' ? '❤️ Interest' :
                             tag.type === 'personality' ? '🧠 Personality' :
                             tag.type === 'occasion' ? '🎉 Occasion' : '🎨 Section'}
                          </span>
                        </td>
                        <td className="p-3 sm:p-4 text-xs sm:text-sm hidden sm:table-cell" style={{ color: '#73766A' }}>
                          {tag.description || '-'}
                        </td>
                        <td className="p-3 sm:p-4 text-xs sm:text-sm hidden md:table-cell" style={{ color: '#9E6752' }}>
                          {tag.product_count || 0}
                        </td>
                        <td className="p-3 sm:p-4">
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                              background: tag.is_active ? '#D1FAE5' : '#FEE2E2',
                              color: tag.is_active ? '#065F46' : '#991B1B'
                            }}
                          >
                            {tag.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-3 sm:p-4">
                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                setTagFormData({
                                  name: tag.name,
                                  description: tag.description,
                                  type: tag.type,
                                  is_active: tag.is_active
                                });
                                setEditingTag(tag);
                                setShowTagDialog(true);
                              }}
                              className="p-1.5 sm:p-2 rounded hover:bg-[#FED7A5]/40 text-[#2D4354]"
                              title="Edit"
                            >
                              <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTagModal({ isOpen: true, tagId: tag.id })}
                              className="p-1.5 sm:p-2 rounded hover:bg-red-100 text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-6 p-4 rounded-xl" style={{ background: '#FFF8F0', borderLeft: '4px solid #9E6752' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <Sparkles className="h-5 w-5" style={{ color: '#9E6752' }} />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold" style={{ color: '#20212B' }}>How Tags Work</h3>
                <div className="mt-2 text-sm" style={{ color: '#534145' }}>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>Product Tags:</strong> General classification for filtering products</li>
                    <li><strong>Interests:</strong> User hobbies (cooking, travel, fitness) - used by AI to match gifts</li>
                    <li><strong>Personality Types:</strong> Character traits (creative, practical, social) - used by AI</li>
                    <li><strong>Occasions:</strong> Gift events (birthday, wedding, graduation) - used by AI</li>
                    <li><strong>Section Tags:</strong> For organizing browse page sections</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md admin-area">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}><Grid3x3 className="w-5 h-5" style={{ color: '#9E6752' }} />{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">AI Prompt (Optional)</label>
              <Input
                value={formData.ai_prompt}
                onChange={(e) => setFormData({...formData, ai_prompt: e.target.value})}
                placeholder="Custom AI behavior for this category"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox" style={{ accentColor: '#9E6752' }}
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <label className="text-sm">Active</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" style={{ background: '#9E6752', color: 'white' }}>
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Browse Section Dialog */}
      <Dialog open={showBrowseSectionDialog} onOpenChange={setShowBrowseSectionDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto admin-area">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}><Layers className="w-5 h-5" style={{ color: '#9E6752' }} />{editingBrowseSection ? 'Edit Browse Section' : 'Add Browse Section'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBrowseSectionSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={browseSectionFormData.title}
                onChange={(e) => setBrowseSectionFormData({...browseSectionFormData, title: e.target.value})}
                required
                placeholder="e.g., For the Tech Enthusiast"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={browseSectionFormData.description}
                onChange={(e) => setBrowseSectionFormData({...browseSectionFormData, description: e.target.value})}
                placeholder="e.g., Gadgets and gear for the digitally savvy"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Categories</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {categories.map(cat => (
                  <label key={cat.id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox" style={{ accentColor: '#9E6752' }}
                      checked={browseSectionFormData.categories.includes(cat.name)}
                      onChange={() => toggleCategoryInBrowseSection(cat.name)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{cat.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Tags (comma-separated)</label>
              <Input
                value={browseSectionFormData.tags}
                onChange={(e) => setBrowseSectionFormData({...browseSectionFormData, tags: e.target.value})}
                placeholder="e.g., tech, gadgets, electronics"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Order</label>
              <Input
                type="number"
                value={browseSectionFormData.order}
                onChange={(e) => setBrowseSectionFormData({...browseSectionFormData, order: parseInt(e.target.value)})}
                min="0"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox" style={{ accentColor: '#9E6752' }}
                checked={browseSectionFormData.is_active}
                onChange={(e) => setBrowseSectionFormData({...browseSectionFormData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <label className="text-sm">Active</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowBrowseSectionDialog(false)}>Cancel</Button>
              <Button type="submit" style={{ background: '#2D4354', color: 'white' }}>
                {editingBrowseSection ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="sm:max-w-md admin-area">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}><Tag className="w-5 h-5" style={{ color: '#9E6752' }} />{editingTag ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTagSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Tag Name *</label>
              <Input
                value={tagFormData.name}
                onChange={(e) => setTagFormData({...tagFormData, name: e.target.value})}
                placeholder="e.g., tech, vintage, handmade"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Used for filtering and AI recommendations</p>
            </div>
            <div>
              <label className="text-sm font-medium">Tag Type *</label>
              <select
                value={tagFormData.type}
                onChange={(e) => setTagFormData({...tagFormData, type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9E6752]"
                required
              >
                <option value="product_tag">🛍️ Product Tag</option>
                <option value="interest">❤️ Interest</option>
                <option value="personality">🧠 Personality Type</option>
                <option value="occasion">🎉 Occasion</option>
                <option value="section_tag">🎨 Section Tag</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {tagFormData.type === 'product_tag' && 'General product classification tags'}
                {tagFormData.type === 'interest' && 'User hobbies and interests (for AI matching)'}
                {tagFormData.type === 'personality' && 'Character traits and styles (for AI matching)'}
                {tagFormData.type === 'occasion' && 'Gift-giving events (for AI matching)'}
                {tagFormData.type === 'section_tag' && 'Tags for organizing browse sections'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={tagFormData.description}
                onChange={(e) => setTagFormData({...tagFormData, description: e.target.value})}
                placeholder="Brief description of this tag"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox" style={{ accentColor: '#9E6752' }}
                checked={tagFormData.is_active}
                onChange={(e) => setTagFormData({...tagFormData, is_active: e.target.checked})}
                className="w-4 h-4"
              />
              <label className="text-sm">Active</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowTagDialog(false)}>Cancel</Button>
              <Button type="submit" style={{ background: '#FED7A5', color: '#20212B' }}>
                {editingTag ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Modals */}
      <DeleteConfirmationModal
        isOpen={deleteCategoryModal.isOpen}
        onClose={() => setDeleteCategoryModal({ isOpen: false, categoryId: null })}
        onConfirm={() => handleDelete(deleteCategoryModal.categoryId)}
        itemName="category"
      />
      <DeleteConfirmationModal
        isOpen={deleteBrowseSectionModal.isOpen}
        onClose={() => setDeleteBrowseSectionModal({ isOpen: false, sectionId: null })}
        onConfirm={() => handleDeleteBrowseSection(deleteBrowseSectionModal.sectionId)}
        itemName="browse section"
      />
      <DeleteConfirmationModal
        isOpen={deleteTagModal.isOpen}
        onClose={() => setDeleteTagModal({ isOpen: false, tagId: null })}
        onConfirm={() => handleDeleteTag(deleteTagModal.tagId)}
        itemName="tag"
      />
    </div>
  );
}
